import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import { EntityChain } from "@/components/cinch/EntityChain";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { ReviewVerdict } from "@/components/cinch/ReviewVerdict";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/lib/auth";

import {
  severityBadgeValue,
  useAddWatch,
  useIncident,
  useIncidentDecomposition,
  useIncidentHistory,
  useRemoveWatch,
  useReviewIncident,
  useTimeseries,
  useWatchlist,
  type Cause,
  type DecompositionRow,
  type IncidentOut,
} from "../api";
import { entityChain, entitySubject } from "../display";
import { DualClockChart } from "./DualClockChart";

const CAUSE_OPTIONS: { value: Cause; label: string }[] = [
  { value: "vendor_outage", label: "Vendor outage" },
  { value: "vendor_data_quality", label: "Vendor data quality" },
  { value: "etl_bug", label: "ETL bug" },
  { value: "expected_change", label: "Expected change (migration, offboarding, seasonal)" },
  { value: "holiday_seasonal", label: "Holiday / seasonal" },
  { value: "other", label: "Other" },
];

/** Quick view: what changed in plain language + the dual-clock chart + the
 *  disposition. Expanded: 180-day chart, which companies explain it, episode
 *  history. AI triage & Claude deep review render as pending until step 5. */
export function IncidentDrawer({
  incidentId,
  presetFlag,
  expanded,
  onExpandedChange,
  onClose,
  onOpenEntity,
}: {
  incidentId: string | null;
  presetFlag: boolean;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
  onOpenEntity: (level: string, keyPath: string) => void;
}) {
  const incident = useIncident(incidentId);
  const canWrite = useCan("integrations.write");
  const review = useReviewIncident();
  const [cause, setCause] = useState<Cause | "">("");
  const row = incident.data;

  const ts = useTimeseries(
    row ? row.level : null,
    row ? row.key_path : null,
    expanded ? 180 : 60,
  );
  const decomposition = useIncidentDecomposition(incidentId, expanded && row?.level === "L1");
  const history = useIncidentHistory(incidentId, expanded);
  const watchlist = useWatchlist();
  const addWatch = useAddWatch();
  const removeWatch = useRemoveWatch();
  const watch = watchlist.data?.find(
    (w) => row && w.level === row.level && w.key_path === row.key_path,
  );

  const decompColumns: DataTableColumn<DecompositionRow>[] = [
    {
      key: "company_name",
      header: "Company",
      render: (r) => r.company_name ?? r.company_id ?? "—",
    },
    { key: "baseline_avg", header: "Normal /day", numeric: true },
    { key: "window_avg", header: "During /day", numeric: true },
    { key: "shortfall_per_day", header: "Missing /day", numeric: true },
  ];

  return (
    <EntityDrawer
      open={incidentId != null}
      onOpenChange={(v) => {
        if (!v && !expanded) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title={row ? (entitySubject(row).name ?? entitySubject(row).id) : "Incident"}
      subtitle={
        row ? `${entitySubject(row).levelLabel} · opened ${row.opened_on}` : ""
      }
    >
      {row && (
        <div className="space-y-4 p-4">
          {/* Where to look, before anything else. The title names only the
              broken thing; on its own "JIFFY LUBE # 1307" does not tell you
              whose store it is or which feed carries it. Ancestors are
              clickable so the drill is the same gesture as reading. */}
          <EntityChain
            links={entityChain(row).map((l) => ({
              id: l.id,
              label: l.levelLabel,
              name: l.name,
              current: l.isSubject,
            }))}
            onLinkClick={(_l, i) => {
              const link = entityChain(row)[i];
              if (link) onOpenEntity(link.level, link.keyPath);
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={severityBadgeValue(row)} />
            <span className="text-xs text-muted-foreground">
              {triggerLabel(row.trigger)}
              {row.reopened_count > 0 && ` · reopened ×${row.reopened_count}`}
              {row.first_signal_on && ` · flagged early on ${row.first_signal_on}`}
            </span>
            <div className="flex-1" />
            {canWrite && (
              <Button
                variant="outline"
                size="sm"
                disabled={addWatch.isPending || removeWatch.isPending}
                onClick={() =>
                  watch
                    ? removeWatch.mutate(watch.id)
                    : addWatch.mutate({ level: row.level, key_path: row.key_path })
                }
              >
                {watch ? (
                  <>
                    <EyeOff className="mr-1 h-3.5 w-3.5" /> Unwatch
                  </>
                ) : (
                  <>
                    <Eye className="mr-1 h-3.5 w-3.5" /> Watch
                  </>
                )}
              </Button>
            )}
          </div>

          <p className="text-sm">{deltaLine(row)}</p>

          <DualClockChart points={ts.data?.points ?? []} />

          <Card className="p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Why this is probably happening
            </p>
            {row.ai_verdict ? (
              <div className="space-y-1 text-sm">
                <StatusBadge value={row.ai_verdict} />
                <p>{row.ai_reasoning}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                AI review pending — the triage layer lands in build step 5.
              </p>
            )}
          </Card>

          <ReviewVerdict
            itemKey={row.id}
            status={row.review_status}
            notes={row.review_notes}
            reviewedByName={row.reviewed_by_name}
            reviewedAt={row.reviewed_at}
            canAct={canWrite}
            saving={review.isPending}
            presetFlag={presetFlag}
            approveLabel="Expected"
            flagLabel="Real issue"
            deniedReason="Needs integrations.write"
            flagPlaceholder="What happened? This feeds detection and future ML."
            extra={
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Cause</span>
                <Select
                  value={cause || row.cause || ""}
                  onValueChange={(v) => setCause(v as Cause)}
                >
                  <SelectTrigger className="h-8 w-full text-xs">
                    <SelectValue placeholder="Pick a cause (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAUSE_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            }
            onSave={({ status, notes }) =>
              review.mutate({
                id: row.id,
                status,
                notes,
                cause: (cause || row.cause || undefined) as Cause | undefined,
              })
            }
          />
          {row.snooze_until && (
            <p className="text-xs text-muted-foreground">
              Snoozed until {row.snooze_until} — resurfaces only if still bad after.
            </p>
          )}

          {expanded && (
            <>
              {row.level === "L1" && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Which companies explain it
                  </h3>
                  <DataTable
                    columns={decompColumns}
                    rows={decomposition.data ?? []}
                    rowKey={(r) => r.company_id ?? r.company_name ?? ""}
                    loading={decomposition.isLoading}
                    onRowClick={(r) =>
                      r.company_id && onOpenEntity("L2", `${row.entity_source}/${r.company_id}`)
                    }
                    emptyMessage="No decomposition available."
                  />
                </div>
              )}
              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Episode history
                </h3>
                <div className="space-y-1.5">
                  {(history.data ?? []).map((h: IncidentOut) => (
                    <div
                      key={h.id}
                      className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm"
                    >
                      <StatusBadge
                        value={h.state === "resolved" ? "resolved" : h.severity}
                      />
                      <span>
                        {h.opened_on} → {h.resolved_on ?? "ongoing"}
                      </span>
                      <span className="text-muted-foreground">
                        {h.streak_days}d · {h.review_status}
                        {h.cause ? ` · ${h.cause}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </EntityDrawer>
  );
}

function triggerLabel(trigger: string): string {
  switch (trigger) {
    case "fast_path":
      return "fast path (total collapse)";
    case "sub_maturity_fast_path":
      return "early fast path (before maturity)";
    case "delivery_silent":
      return "pipe went silent";
    case "watchlist":
      return "watchlist";
    default:
      return "confirmed (2 bad days)";
  }
}

function deltaLine(r: IncidentOut): string {
  const name = r.entity_source ?? r.key_path;
  const magnitude =
    r.magnitude != null ? `~${Math.round(r.magnitude).toLocaleString()} txns/day missing` : "";
  if (r.state === "dormant") {
    return `${name}: effectively no volume since ${r.last_bad_day ?? r.opened_on} — one standing row, not daily alerts.`;
  }
  const span = r.streak_days > 1 ? `for ${r.streak_days} days` : "yesterday";
  return `${name}: below normal ${span}${magnitude ? ` — ${magnitude}` : ""}.`;
}
